import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { RootState, addPlayer, setCurrentTime, setBackgroundImageUrl, updatePlayerProps, setIsPlaying, setDuration, addKeyframe, removeKeyframe } from './store';

// Used to signal CanvasBoard to run export externally
export const triggerExportEvent = new EventTarget();

export const Sidebar: React.FC = () => {
    const dispatch = useDispatch();
    const { players, selectedPlayerId } = useSelector((state: RootState) => state.app);
    const selectedPlayer = players.find(p => p.id === selectedPlayerId);

    const [color, setColor] = useState('#ff0000');
    const [shape, setShape] = useState<'circle' | 'triangle'>('circle');
    const [label, setLabel] = useState('1');

    useEffect(() => {
        if (selectedPlayer) {
            setColor(selectedPlayer.color);
            setShape(selectedPlayer.shape);
            setLabel(selectedPlayer.label);
        }
    }, [selectedPlayer]);

    const handleAddPlayer = () => {
        dispatch(addPlayer({
            id: `p_${Date.now()}`,
            shape,
            color,
            label,
            initialX: 100,
            initialY: 300
        }));
    };

    const handleUpdatePlayer = () => {
        if (selectedPlayer) {
            dispatch(updatePlayerProps({ id: selectedPlayer.id, color, shape, label }));
        }
    };

    const defaultBackgrounds = useMemo(() => ([
        { name: 'Národní házená', url: '/elektronicka-tuzka-narodni-hazene/Hriste-Narodka.png' },
        { name: 'Národní házená (brankoviště)', url: '/elektronicka-tuzka-narodni-hazene/Hriste-Narodka-Brankoviste.png' },
    ]), []);

    useEffect(() => {
        dispatch(setBackgroundImageUrl(defaultBackgrounds[0]?.url ?? null));
    }, [dispatch, defaultBackgrounds]);

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                dispatch(setBackgroundImageUrl(event.target?.result as string));
            };
            reader.readAsDataURL(file);
        }
    };

    return (
        <div className="w-full md:w-64 bg-white text-gray-800 p-4 h-full flex flex-col overflow-y-auto border-r border-gray-200">
            <h2 className="text-xl font-bold mb-4 text-gray-900">Background</h2>
            <div className="mb-4">
                <label className="block text-sm mb-1 font-semibold">Select</label>
                <select className="w-full bg-gray-50 border border-gray-300 p-3 md:p-2 rounded text-sm text-gray-900 border-2" onChange={(e) => dispatch(setBackgroundImageUrl(e.target.value))}>
                    {defaultBackgrounds.map((bg, idx) => (
                        <option key={idx} value={bg.url}>{bg.name}</option>
                    ))}
                </select>
            </div>
            <div className="mb-6 border-b border-gray-200 pb-4">
                <label className="block text-sm mb-1 font-semibold">Upload Image</label>
                <input type="file" accept="image/*" onChange={handleImageUpload} className="w-full text-sm text-gray-600 p-2 md:p-0" />
            </div>

            <h2 className="text-xl font-bold mb-4 text-gray-900">{selectedPlayer ? 'Edit Player' : 'Add Player'}</h2>
            <div className="mb-4">
                <label className="block text-sm mb-1 font-semibold">Color (Hex)</label>
                <input type="color" value={color} onChange={(e) => {
                    setColor(e.target.value);
                    if (selectedPlayer) dispatch(updatePlayerProps({ id: selectedPlayer.id, color: e.target.value }));
                }} className="w-full h-10 p-1 bg-white border border-gray-300 rounded" />
            </div>
            <div className="mb-4">
                <label className="block text-sm mb-1 font-semibold">Shape</label>
                <select value={shape} onChange={(e) => {
                    const newShape = e.target.value as 'circle' | 'triangle';
                    setShape(newShape);
                    if (selectedPlayer) dispatch(updatePlayerProps({ id: selectedPlayer.id, shape: newShape }));
                }} className="w-full bg-white border border-gray-300 p-3 md:p-2 rounded text-gray-900 border-2">
                    <option value="circle">Circle</option>
                    <option value="triangle">Triangle</option>
                </select>
            </div>
            <div className="mb-4">
                <label className="block text-sm mb-1 font-semibold">Label / Number</label>
                <input type="text" value={label} onChange={(e) => {
                    setLabel(e.target.value);
                    if (selectedPlayer) dispatch(updatePlayerProps({ id: selectedPlayer.id, label: e.target.value }));
                }} className="w-full bg-white border border-gray-300 p-3 md:p-2 rounded text-gray-900 border-2" />
            </div>

            {!selectedPlayer && (
                <button onClick={handleAddPlayer} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded shadow-sm transition-colors">
                    Spawn Player
                </button>
            )}
            {selectedPlayer && (
                <p className="text-sm text-gray-500 italic mt-2">Deselect (click empty space on board) to add new.</p>
            )}
        </div>
    );
};

export const Timeline: React.FC = () => {
    const dispatch = useDispatch();
    const { currentTime, duration, isPlaying, keyframes } = useSelector((state: RootState) => state.app);

    const allDataPoints = [0, duration];
    keyframes.forEach(k => allDataPoints.push(k.time));
    const uniqueDots = Array.from(new Set(allDataPoints)).sort((a, b) => a - b);

    const requestRef = useRef<number>();
    const lastTimeRef = useRef<number>();

    const animateParamsRef = useRef({ isPlaying, currentTime, duration });
    useEffect(() => {
        animateParamsRef.current = { isPlaying, currentTime, duration };
    }, [isPlaying, currentTime, duration]);

    useEffect(() => {
        const animate = (time: number) => {
            const params = animateParamsRef.current;
            if (params.isPlaying) {
                if (lastTimeRef.current !== undefined) {
                    const deltaTime = time - lastTimeRef.current;
                    let newTime = params.currentTime + deltaTime;
                    if (newTime >= params.duration) {
                        newTime = params.duration;
                        dispatch(setIsPlaying(false));
                    }
                    dispatch(setCurrentTime(newTime));
                }
                lastTimeRef.current = time;
            } else {
                lastTimeRef.current = undefined;
            }
            requestRef.current = requestAnimationFrame(animate);
        };
        requestRef.current = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(requestRef.current!);
    }, [dispatch]);

    const handleCaptureFrame = () => triggerExportEvent.dispatchEvent(new Event('captureFrame'));

    return (
        <div className="h-auto md:h-24 bg-gray-50 text-gray-800 p-2 md:p-4 flex flex-col md:flex-row items-center justify-between shadow-inner border-t border-gray-200">
            <div className="mr-0 md:mr-4 mb-2 md:mb-0 w-full md:w-auto flex justify-center">
                <button onClick={() => dispatch(setIsPlaying(!isPlaying))} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 md:py-2 font-bold rounded shadow-sm w-full md:w-auto">
                    {isPlaying ? 'Pause' : 'Play'}
                </button>
            </div>
            <div className="flex-1 flex flex-col justify-center relative px-2 w-full mb-2 md:mb-0">
                <label className="text-sm font-bold mb-2 text-gray-700">Timeline: {(currentTime / 1000).toFixed(2)}s / {(duration / 1000).toFixed(2)}s</label>
                <div className="relative w-full h-[20px] md:h-[6px] mb-2 flex items-center">
                    <input
                        type="range"
                        min={0}
                        max={duration}
                        step={10}
                        value={currentTime}
                        onChange={(e) => dispatch(setCurrentTime(Number(e.target.value)))}
                        className="absolute w-full h-full opacity-50 z-10 cursor-pointer accent-blue-600"
                    />
                    <div className="absolute w-full h-2 md:h-1 bg-gray-300 top-1/2 left-0 transform -translate-y-1/2 rounded pointer-events-none">
                        {uniqueDots.map(t => (
                            <div key={t} className="absolute w-2 h-2 md:w-1.5 md:h-1.5 bg-blue-500 rounded-full top-1/2 transform -translate-y-1/2 -ml-[4px] md:-ml-[3px]" style={{ left: `${duration > 0 ? (t / duration) * 100 : 0}%` }} />
                        ))}
                    </div>
                </div>
            </div>
            <div className="ml-0 md:ml-4 flex flex-wrap justify-center gap-2 items-center w-full md:w-auto">
                <div className="flex flex-col items-center">
                    <label className="text-xs font-bold text-gray-500 uppercase">Duration (s)</label>
                    <input
                        type="number"
                        value={duration / 1000}
                        onChange={(e) => dispatch(setDuration(Number(e.target.value) * 1000))}
                        className="w-20 md:w-16 bg-white border border-gray-300 text-gray-900 text-sm md:text-xs p-2 md:p-1 rounded font-semibold text-center"
                    />
                </div>
                <button onClick={handleCaptureFrame} className="bg-green-600 hover:bg-green-700 text-white px-3 py-3 md:px-2 md:py-2 font-bold rounded text-sm md:text-xs ml-2 shadow-sm flex-1 md:flex-none">
                    Capture Frame
                </button>
                <button onClick={() => dispatch(removeKeyframe(currentTime))} className="bg-red-500 hover:bg-red-600 text-white px-3 py-3 md:px-2 md:py-2 font-bold rounded text-sm md:text-xs shadow-sm flex-1 md:flex-none" disabled={currentTime === 0 && keyframes.length === 1}>
                    Remove Keyframe
                </button>
                <button onClick={() => triggerExportEvent.dispatchEvent(new Event('exportVideo'))} className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-3 md:px-2 md:py-2 font-bold rounded text-sm md:text-xs shadow-sm flex-1 md:flex-none">
                    Export MP4
                </button>
                <button onClick={() => triggerExportEvent.dispatchEvent(new Event('exportZip'))} className="bg-purple-500 hover:bg-purple-600 text-white px-3 py-3 md:px-2 md:py-2 font-bold rounded text-sm md:text-xs shadow-sm flex-1 md:flex-none">
                    Export ZIP
                </button>
            </div>
        </div>
    );
};